import { FrequencyDetectionStrategy } from '~/modules/GameEngine/Input/MicStrategies/interfaces';

export default class AubioStrategy implements FrequencyDetectionStrategy {
  private detector: any;

  // Para suavizar: si durante unos frames aubio devuelve 0,
  // mantenemos el último pitch válido un poco.
  private lastGoodFreq = 0;
  private lastGoodAt = 0;

  public init = async (context: AudioContext, buffer: number): Promise<void> => {
    const aubio = await import('aubiojs');
    const { Pitch } = await aubio.default();

    // 🔧 Cambiar algoritmo: "yin" suele ser más estable que "default"
    // Otros posibles: "yinfft", "mcomb", "fcomb", "schmitt"
    const algo = 'yin';

    // buffer = windowSize
    // buffer/8 = hopSize (ya lo tenías)
    this.detector = new Pitch(algo, buffer, buffer / 8, context.sampleRate);

    // 🔧 tolerance:
    // - más alto => más estricto (devuelve 0 más a menudo)
    // - más bajo => más permisivo (puede dar más “saltos”)
    // 0.5 es “medio”; prueba 0.35 si sigue devolviendo 0 mucho
    this.detector.setTolerance(0.35);

    // 🔧 silence (si existe): muchos builds de aubio lo soportan
    // Valores típicos: -40 a -70. (Más cerca de 0 = más estricto)
    if (typeof this.detector.setSilence === 'function') {
      this.detector.setSilence(-50);
    }

    // Opcional: si existe setUnit, forzamos Hz
    if (typeof this.detector.setUnit === 'function') {
      this.detector.setUnit('Hz');
    }

    // Reset smoothing state
    this.lastGoodFreq = 0;
    this.lastGoodAt = performance.now();
  };

  public getSampleSize(): number {
    return 1 << 11;
  }

  public getFrequency = async (data: Float32Array) => {
    // aubio.do puede devolver number o algo raro según build
    let f = 0;

    try {
      f = this.detector.do(data);
    } catch {
      // si por lo que sea peta, devolvemos 0
      return 0;
    }

    // Normalizar
    if (!Number.isFinite(f)) f = 0;

    // Limitar rango humano (aprox). Ajusta si quieres:
    // voz masculina ~85–180 Hz, femenina ~165–255 Hz, pero cantando se sale.
    const MIN_HZ = 50;
    const MAX_HZ = 2000;

    const now = performance.now();

    // Si aubio devuelve un valor “bueno”, lo guardamos
    if (f >= MIN_HZ && f <= MAX_HZ) {
      this.lastGoodFreq = f;
      this.lastGoodAt = now;
      return f;
    }

    // Si devuelve 0 o basura:
    // ✅ mantenemos el último valor durante un pequeño “hold”
    // para evitar que el scoring caiga a 0 por microcortes del device "default".
    const HOLD_MS = 250; // 0.25s
    if (this.lastGoodFreq > 0 && now - this.lastGoodAt <= HOLD_MS) {
      return this.lastGoodFreq;
    }

    return 0;
  };
}
